import React, {FC, memo, ReactNode, useMemo, useState} from 'react';
import {useTranslations} from '@montana/containers/translations';
import {ProductImage} from '../product-image/product-image.component';
import {IImage, ILink, IProductPrices, IVariantCardDimension} from '@montana/models/generated/cms.interface';
import {Link} from '../link';
import {Media, MontanaMediaRatios} from '../media';
import {useTracking} from '@montana/containers/tracking';
import {IGtmProductFromList} from '@montana/services/tracking/tracking';
import {Splash} from '../splash/splash.component';
import cx from 'clsx';
import clsx from 'clsx';
import {getDimensionTranslationValue} from '@montana/helpers/product.helper';
import {StrokeLine} from '@montana/shared/stroke';
import {CylindoDefaultView, CylindoFrontView, LDDefaultView, LDFrontView} from '@montana/constants/images';
import {PriceFormatted} from "@montana/shared/price-formatted/price-formatted";
import {ColorSwatch} from "@montana/shared/color-swatch";
import {IColorExtended} from "@montana/helpers/color.helper";

interface IProductCardProps {
    className?: string;
    customImage?: IImage;
    imageUrl?: string;
    dimensions?: IVariantCardDimension[];
    gtmProduct?: IGtmProductFromList;
    link?: ILink;
    overrideInformation?: ReactNode;
    price?: IProductPrices;
    priceOverride?: ReactNode;
    sizes?: string;
    title?: string;
    colourSwatches?: IColorExtended[];
    numberOfVariantColours?: number;
}

export const ProductCard: FC<IProductCardProps> = memo((props: IProductCardProps) => {
        const {
            className,
            customImage,
            imageUrl,
            dimensions,
            gtmProduct,
            link,
            overrideInformation,
            price,
            priceOverride,
            sizes,
            title,
            colourSwatches,
            numberOfVariantColours,
        } = props;

        const translate = useTranslations();
        const {trackProductClick} = useTracking();
        const [isHovered, setIsHovered] = useState<boolean>(false);
        const [isViewTextHovered, setIsViewTextHovered] = useState<boolean>(false);

        const onClick = () => {
            if (gtmProduct) {
                trackProductClick(price?.priceWithDiscount?.currency || '', gtmProduct, 'category');
            }
        };

        const DisplayAvailableColors = useMemo(() => {
            if (!colourSwatches || !colourSwatches.length || !numberOfVariantColours) {
                return null;
            }
            const remainingColours = numberOfVariantColours - colourSwatches.length;
            return (
                <div className="product-card__colors">
                    {colourSwatches.map((color, index) => {
                            if (color.hexCode) {
                                return (
                                    <ColorSwatch key={`${gtmProduct?.id}-${color.colorId}-${index}`} variant="row"
                                                 hexColor={color.hexCode}/>
                                )
                            } else if (color.colorSwatchImage?.url) {
                                return (
                                    <ColorSwatch key={`${gtmProduct?.id}-${color.colorId}-${index}`} variant="row"
                                                 colorSwatchImage={color.colorSwatchImage}/>

                                )
                            } else {
                                return null;
                            }

                        }
                    )}
                    {numberOfVariantColours && remainingColours > 0 && (<span
                        className="product-card__colors-more">+{remainingColours}</span>)
                    }
                </div>)

        }, [colourSwatches, numberOfVariantColours, gtmProduct?.id])

        return (
            <Link
                className={`product-card${className ? ` ${className}` : ''}`}
                to={link?.url}
                title={link?.title}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={onClick}
            >
                <Splash {...price?.splash} />
                <div
                    className={`product-card__image-wrapper${!!customImage ? ' product-card__image-wrapper--custom-image' : ''}`}>
                    {!!customImage && (
                        <Media
                            image={customImage}
                            mediaRatio={MontanaMediaRatios.Portrait}
                            placeholder={false}
                            animate={true}
                            sizes={sizes}
                        />
                    )}
                    {!customImage && (
                        <div className="product-card__images">
                            <ProductImage
                                className={cx({'product-card__hide-image': isHovered})}
                                url={imageUrl}
                                ariaHidden={true}
                                frame={imageUrl?.includes('londondynamics') ? LDDefaultView : CylindoDefaultView}
                                name={title}
                                sizes={sizes}
                            />
                            <ProductImage
                                className={cx({'product-card__hide-image': !isHovered})}
                                url={imageUrl}
                                ariaHidden={true}
                                frame={imageUrl?.includes('londondynamics') ? LDFrontView : CylindoFrontView}
                                name={title}
                                sizes={sizes}
                                animate={false}
                            />
                        </div>
                    )}
                    {!customImage && isHovered &&
                        <div
                            className="product-card__view-text"
                            onMouseEnter={() => setIsViewTextHovered(true)}
                            onMouseLeave={() => setIsViewTextHovered(false)}
                        >
                            {translate((t): string => t.filterAndSearch.view, "View")}
                            <StrokeLine active={isViewTextHovered} inverseAnimation={true}/>
                        </div>
                    }
                    {!!(colourSwatches?.length) && DisplayAvailableColors}
                </div>
                <div
                    className={clsx('product-card__information', {
                        ['product-card__information--no-dimensions']: !dimensions?.length
                    })}
                >
                    {overrideInformation ?
                        overrideInformation :
                        (
                            <>
                                <h3 className="product-card__information-heading">{title}</h3>
                                {!!dimensions?.length && dimensions.slice(0, 3).map((dim) => (
                                    <span className="product-card__information-specs" key={dim.dimensionValue}>
                                    {dim.dimensionName}: {getDimensionTranslationValue(translate, dim.dimensionValue)}
                                </span>
                                ))}
                                {priceOverride ? priceOverride : (
                                    <>
                                        {!!price?.priceWithDiscount && (
                                            <div className="product-card__information-prices">
                                            <span
                                                className="product-card__information-current-price">{translate((t): string => t.filterAndSearch.shownModel, "Shown model")}
                                                <PriceFormatted currency={price?.priceWithDiscount?.currency}
                                                                amount={price?.priceWithDiscount?.formatted}/></span>
                                                {price.beforePrice && price.beforePrice.amount > price.priceWithDiscount.amount && (
                                                    <span
                                                        className="product-card__information-before-price">{price?.beforePrice?.formattedWithCurrency}</span>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        )
                    }
                </div>
            </Link>
        )
    })
;
